/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/**
 * Patient-delivery helpers shared by the Medaica Viewer page and the Medaica
 * Report page: the patient-facing document and the provider document built
 * from a saved report, the masked form of the recipient's address shown before sending, and the
 * house API error type of a failed request.
 */

import moment from "moment"
import { buildPatientReportDocument } from "@medaica/common/components/pcg-report/patient-report-document"
import { buildProviderReportDocument } from "@medaica/common/components/pcg-report/provider-report-document"
import { isMedaicaApiError, MedaicaApiErrorType } from "@medaica/common/services/medaica-api-service"
import {
  PcgPatientReportDocument,
  PcgProviderReportDocument,
} from "@medaica/common/services/medaica-api-service/pcg-review-types"

/**
 * The patient-facing document of a Medaica Report: the report payload
 * flattened with the patient view's own rules, headed by the patient's name
 * and the exam date as the report prints it.
 */
const buildExamPatientDocument = (
  reportPayload: unknown,
  { patientName, examDateCreated }: { patientName: string; examDateCreated: Date | null | undefined }
): PcgPatientReportDocument =>
  buildPatientReportDocument(reportPayload, {
    patientName,
    examDate: moment(examDateCreated).format("MMM D, YYYY"),
  })

/**
 * The provider document of a Medaica Report: the report payload flattened with
 * the provider view's own rules, headed like the on-screen provider document
 * — the same exam date as the patient document, and the demographics the
 * report page shows. The document goes to the patient (to share with their
 * own doctor), so the name is the patient's real name, the one the patient
 * document carries, rather than the page's cross-organization mask.
 */
const buildExamProviderDocument = (
  reportPayload: unknown,
  {
    patientName,
    examDateCreated,
    demographics = {},
  }: {
    patientName: string
    examDateCreated: Date | null | undefined
    demographics?: { dob?: string; age?: string; sex?: string; patientId?: string }
  }
): PcgProviderReportDocument =>
  buildProviderReportDocument(reportPayload, {
    patientName,
    examDate: moment(examDateCreated).format("MMM D, YYYY"),
    dob: demographics.dob,
    age: demographics.age,
    sex: demographics.sex,
    patientId: demographics.patientId,
  })

/**
 * "jane@example.com" → "j•••@example.com": enough for the clinician to
 * recognise the address the report goes to without the page spelling it out.
 */
const maskEmailAddress = (emailAddress: string): string => {
  const at = emailAddress.indexOf("@")
  if (at < 1) {
    return "•••"
  }
  return `${emailAddress[0]}•••${emailAddress.slice(at)}`
}

/** The house error type carried by a failed API request, or null for any other failure. */
const apiErrorTypeOf = (error: unknown): MedaicaApiErrorType | null =>
  error !== null && isMedaicaApiError(error) ? (error as { type: MedaicaApiErrorType }).type : null

/** The HTTP status of a failed API request, or null when the failure never reached a response. */
const responseStatusOf = (error: unknown): number | null =>
  (error as { response?: { status?: number } } | null)?.response?.status ?? null

export { apiErrorTypeOf, buildExamPatientDocument, buildExamProviderDocument, maskEmailAddress, responseStatusOf }
